import { Outlet } from '@tanstack/react-router';

import { Skeleton } from '@/components/ui/skeleton';
import { SectionLayout } from '@/features/app-shell/components/SectionLayout';
import { SETTINGS_TABS, type NavItem } from '@/features/app-shell/navigation';
import { useAccessRequests, useWorkspace } from '@/features/workspaces';
import { PageHeader } from '@/shared/components/PageHeader';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { RetryButton } from '@/shared/components/RetryButton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { useWorkspaceMembers } from '../hooks/useWorkspaceMembers';
import { SettingsBandPills } from './SettingsBandPills';

/** What every tab is about, under the band's title. */
const DESCRIPTION =
  'Its name, who can see it, its people, the labels its tasks can carry, and the images its work runs on.';

/** A tab's placeholder while the workspace is read (spec 194 US5). */
function TabSkeleton() {
  return (
    <PendingRegion label="Loading settings" className="gap-3">
      <Skeleton className="h-4 w-28 rounded-sm" />
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="flex items-center gap-3 py-1">
          <Skeleton className="size-7 rounded-full" />
          <Skeleton className="h-4 w-full max-w-sm rounded-sm" />
        </div>
      ))}
    </PendingRegion>
  );
}

/**
 * Workspace settings as a grouped entry (spec 227): the band titled "Settings",
 * the workspace's name, visibility and this person's role as its pills, and the
 * tabs as addresses under it — General, People, Activity, Labels and Execution
 * images — drawn by the same `SectionLayout` Integrations and AI usage use.
 *
 * It was a page with its own tab switch and the name as its title (spec 194).
 * Each tab is a route now, so a link, Back, the breadcrumb and the palette treat
 * them as they treat every other group's tabs; the name stands beside the title
 * and is renamed from General (FR-004, FR-005).
 *
 * The layout reads the workspace once, for the band and for the People link's
 * count and "waiting to join" badge (FR-010); the tabs read what they show under
 * the same cache keys. While that read is pending the tabs' place holds a
 * skeleton; when it fails, the failure and a Retry — the band says "Settings"
 * throughout.
 */
export function SettingsLayout({ workspaceId }: { workspaceId: string }) {
  const {
    data: workspace,
    isPending,
    isError,
    refetch,
  } = useWorkspace(workspaceId);
  const showPlaceholder = usePendingDisplay(isPending);
  const access = workspace?.access;
  const canWork = access?.can_work ?? true;
  const canManage = access?.can_manage ?? true;
  const members = useWorkspaceMembers(
    workspaceId,
    workspace != null && canWork,
  );
  // The same read as the People tab's, so it costs nothing twice.
  const requests = useAccessRequests(
    workspaceId,
    workspace != null && canManage,
  );
  const memberCount = members.data?.members.length;
  const waiting = canManage ? requests.items.length : 0;
  const tabs: NavItem[] = SETTINGS_TABS.map((tab) =>
    tab.to === '/workspaces/$workspaceId/settings/people'
      ? {
          ...tab,
          tabBadge: {
            count: memberCount,
            attention: { count: waiting, label: 'waiting to join' },
          },
        }
      : tab,
  );

  const failed = !isPending && (isError || workspace == null);

  return (
    <SectionLayout
      title="Settings"
      description={DESCRIPTION}
      surface="inverse"
      tabs={tabs}
      workspaceId={workspaceId}
    >
      <section className="flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6 pb-12">
        <PageHeader
          eyebrow="Workspace"
          title="Settings"
          aside={<SettingsBandPills workspace={workspace ?? undefined} />}
        />
        <div className="flex flex-col gap-8">
          {isPending ? (
            showPlaceholder ? (
              <TabSkeleton />
            ) : null
          ) : failed ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-muted-foreground text-sm">
                This workspace&rsquo;s settings could not be read just now.
                Nothing has changed; try again in a moment.
              </p>
              <RetryButton onRetry={() => void refetch()} />
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </section>
    </SectionLayout>
  );
}
